import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import { format } from 'date-fns';
import { trialRequestService } from '../../services/api';
import { TRIAL_STATUSES, statusLabel, statusClass } from './trialStatus';
import './TrialDetail.css';

const dt = (d) => (d ? format(new Date(d), 'dd MMM yyyy, HH:mm') : '—');
const day = (d) => (d ? format(new Date(d), 'dd MMM yyyy') : '—');
const inputDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');

function Section({ title, rows }) {
  return (
    <div className="card trial-card">
      <h3>{title}</h3>
      <dl className="trial-kv">
        {rows.map(([k, v, mono]) => (<React.Fragment key={k}><dt>{k}</dt><dd className={mono ? 'mono' : undefined}>{v || '—'}</dd></React.Fragment>))}
      </dl>
    </div>
  );
}

export default function TrialDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tr, setTr] = useState(null);
  const [dates, setDates] = useState({ trialStart: '', trialEnd: '' });
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const res = await trialRequestService.getById(id);
    const r = res.data.request;
    setTr(r);
    setDates({ trialStart: inputDate(r.trialStart), trialEnd: inputDate(r.trialEnd) });
  }, [id]);
  useEffect(() => { load().catch(() => setError('Trial request not found')); }, [load]);

  const patch = async (body) => {
    setSaving(true); setError('');
    try { await trialRequestService.update(id, body); await load(); }
    catch (err) { setError(err.response?.data?.message || 'Update failed'); }
    finally { setSaving(false); }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    await trialRequestService.addNote(id, note.trim());
    setNote(''); load();
  };

  const downloadPdf = async () => {
    const res = await trialRequestService.pdf(id);
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = url; a.download = `${tr.reference}-Trial-Agreement.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!tr) return <div className="empty-state">{error || 'Loading...'}</div>;

  return (
    <div className="trial-detail">
      <button className="btn btn-ghost" onClick={() => navigate('/trials')}><ArrowLeft size={14} /> Back to trial requests</button>
      <div className="page-header" style={{ marginTop: 16 }}>
        <div>
          <h1 className="page-title">{tr.company}</h1>
          <p className="page-subtitle"><span className="ticket-num">{tr.reference}</span> · {tr.product} · submitted {dt(tr.emailVerifiedAt)}</p>
        </div>
        <button className="btn btn-ghost" onClick={downloadPdf}><Download size={14} /> Agreement PDF</button>
      </div>

      {error && <div className="trial-error">{error}</div>}

      <div className="card trial-card">
        <h3>Handling</h3>
        <div className="trial-handling">
          <label>Status
            <select value={tr.status} disabled={saving} onChange={e => patch({ status: e.target.value })}>
              {TRIAL_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label>Trial start
            <input type="date" value={dates.trialStart} onChange={e => setDates(d => ({ ...d, trialStart: e.target.value }))} />
          </label>
          <label>Trial end
            <input type="date" value={dates.trialEnd} onChange={e => setDates(d => ({ ...d, trialEnd: e.target.value }))} />
          </label>
          <button className="btn btn-primary" disabled={saving} onClick={() => patch(dates)}>Save dates</button>
        </div>
        <div className="text-muted" style={{ fontSize: 12, marginTop: 8 }}>
          Approving or activating requires both trial dates. Current: <span className={`badge ${statusClass(tr.status)}`}>{statusLabel(tr.status)}</span>
        </div>
      </div>

      <div className="trial-grid">
        <Section title="Customer" rows={[
          ['Company', tr.company], ['Registered address', tr.address], ['GSTIN', tr.gstin], ['Plant / site', tr.site],
        ]} />
        <Section title="Contact" rows={[
          ['Name', tr.contactName], ['Designation', tr.designation], ['Email', tr.email], ['Mobile', tr.phone],
        ]} />
        <Section title="Trial" rows={[
          ['Plan (demo)', tr.plan], ['Users', String(tr.users)], ['Preferred start', day(tr.preferredStart)],
          ['Trial dates', tr.trialStart && tr.trialEnd ? `${day(tr.trialStart)} to ${day(tr.trialEnd)}` : 'Not set'],
          ['Evaluation goals', tr.goals],
        ]} />
        <Section title="Acceptance record" rows={[
          ['Agreement version', `v${tr.agreementVersion}`], ['Fingerprint', tr.agreementHash, true],
          ['Terms accepted at', dt(tr.acceptedAt), true], ['Email verified at', dt(tr.emailVerifiedAt), true],
          ['IP address', tr.verifiedIp || tr.acceptedIp, true], ['Reference consent', tr.consents?.customerReference ? 'Given (Section 8)' : 'Not given'],
        ]} />
      </div>

      <div className="card trial-card" style={{ marginTop: 16 }}>
        <h3>Internal notes</h3>
        {(tr.notes || []).length === 0 && <div className="text-muted" style={{ fontSize: 13 }}>No notes yet.</div>}
        {(tr.notes || []).map((n, i) => (
          <div key={i} className="trial-note"><div className="trial-note__meta">{n.authorName} · {dt(n.createdAt)}</div>{n.text}</div>
        ))}
        <form onSubmit={addNote} style={{ display: 'grid', gap: 8, marginTop: 12 }}>
          <textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Add an internal note (not visible to the prospect)" />
          <div><button className="btn btn-primary" type="submit" disabled={!note.trim()}>Add note</button></div>
        </form>
      </div>

      {(tr.statusHistory || []).length > 0 && (
        <div className="card trial-card">
          <h3>Status history</h3>
          {tr.statusHistory.map((h, i) => (<div key={i} style={{ fontSize: 13, padding: '2px 0' }}>{dt(h.at)} · <strong>{statusLabel(h.status)}</strong> · {h.by}</div>))}
        </div>
      )}
    </div>
  );
}
